/**
 * The launch moment, in one place.
 *
 * The date was written into the hero chip as a string. The countdown has to agree with
 * that chip to the day, and two hand-typed dates drift the moment one of them is edited,
 * so both now read from here.
 *
 * Stored as an ISO instant with an explicit offset rather than a local date: a visitor in
 * Auckland and a visitor in Los Angeles should see the same number of days remaining, not
 * a number derived from their own midnight. -06:00 is CT, which is where the studio is.
 */
export const LAUNCH_AT = '2027-01-01T00:00:00-05:00';

/** The zone the launch is announced in. January is EST, hence -05:00 above. */
export const LAUNCH_TZ = 'America/New_York';
export const LAUNCH_TZ_LABEL = 'Eastern';

/** Rendered in the hero chip. Keep it consistent with LAUNCH_AT. */
export const LAUNCH_LABEL = 'January 1, 2027';

export const launchDate = () => new Date(LAUNCH_AT);

/**
 * The launch instant, written out in one time zone.
 *
 * The countdown itself needs none of this: it measures a duration to a fixed instant,
 * and a duration is the same length everywhere, so a visitor in Sydney and one in
 * Seattle see identical digits. What DOES differ is the wall-clock date that instant
 * lands on. Midnight Eastern is 9pm on December 31 in California, so telling that
 * visitor "January 1" is wrong by a calendar day.
 *
 * The visitor's zone comes from the browser, which already knows it exactly. An IP
 * lookup would be a network round trip, a third party in the request path, and a wrong
 * answer for anyone on a VPN, in exchange for a worse guess at the same fact.
 */
const formatIn = (timeZone) =>
  new Intl.DateTimeFormat('en-US', {
    timeZone,
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(launchDate());

export const launchInLaunchZone = () => formatIn(LAUNCH_TZ);

/** Null when the visitor's zone renders the same moment identically, so callers can
 *  skip telling an Eastern reader their local time is their local time. */
export const launchInVisitorZone = () => {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!zone) return null;
    const local = formatIn(zone);
    return local === launchInLaunchZone() ? null : local;
  } catch (_) {
    return null; // Intl unavailable or zone undetectable: say nothing rather than guess.
  }
};

/**
 * Whole units remaining until launch, floored, never negative. Returns null once the
 * moment has passed so callers can render nothing rather than a frozen row of zeros.
 */
export const timeUntilLaunch = (now = Date.now()) => {
  const ms = launchDate().getTime() - now;
  if (ms <= 0) return null;
  const s = Math.floor(ms / 1000);
  return {
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60,
  };
};
